import { queryOptions } from '@tanstack/react-query';
import type { ClientsListFilter } from '../lib/filters';
import type { ClientType } from '../model/schema';
import { clientApi } from './client-api';

/**
 * Query-key factory + query-options for the legacy client domain. Mutations
 * invalidate through `clientKeys` (never an inline array).
 */
export const clientKeys = {
  all: ['legacy-clients'] as const,
  lists: () => [...clientKeys.all, 'list'] as const,
  list: (clientType: ClientType, filter: ClientsListFilter) =>
    [...clientKeys.lists(), clientType, filter] as const,
  counts: (filter: ClientsListFilter) => [...clientKeys.all, 'counts', filter] as const,
  tariffs: () => [...clientKeys.all, 'tariffs'] as const,
  admins: (roles: string) => [...clientKeys.all, 'admins', roles] as const,
};

function listQueryFn(clientType: ClientType, filter: ClientsListFilter) {
  if (clientType === 'client') return () => clientApi.clients(filter);
  if (clientType === 'employees') return () => clientApi.employees(filter);
  return () => clientApi.vendorsWithOrders(filter);
}

export const clientQueries = {
  /** Segment list; only the active segment's endpoint is ever called. */
  list: (clientType: ClientType, filter: ClientsListFilter, enabled = true) =>
    queryOptions({
      queryKey: clientKeys.list(clientType, filter),
      queryFn: listQueryFn(clientType, filter),
      enabled,
      placeholderData: (prev) => prev,
    }),

  tariffs: (enabled = true) =>
    queryOptions({
      queryKey: clientKeys.tariffs(),
      queryFn: () => clientApi.tariffs(),
      enabled,
      staleTime: 5 * 60 * 1000,
    }),

  admins: (roles: string, enabled = true) =>
    queryOptions({
      queryKey: clientKeys.admins(roles),
      queryFn: () => clientApi.admins({ roles }),
      enabled: enabled && Boolean(roles),
      staleTime: 5 * 60 * 1000,
    }),
};
